import { useState, useEffect } from 'react';
import { translateText } from './useTranslation';
import { searchWord } from '../services/dictionary';
import { getWordTranslationOverride } from '../data/wordNuances';
import type { WordDetails } from '../types';

export function useWordDetails(word: string, context?: string) {
  const [details, setDetails] = useState<WordDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchWordDetails() {
      if (!word.trim()) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Use the updated searchWord function that handles both APIs
        const wordData = await searchWord(word.trim());
        
        if (!wordData || !wordData.definition) {
          throw new Error('Word not found');
        }

        const lowerWord = word.trim().toLowerCase();
        const isSingleWord = lowerWord.split(/\s+/).length === 1;
        const translationOverride = getWordTranslationOverride(lowerWord);
        let translation = translationOverride || wordData.translation;
        if (!translationOverride && (!translation || (isSingleWord && !translation.includes('/') && !translation.includes(',')))) {
          const enriched = await translateText(word.trim(), 'en', 'pt');
          if (enriched) {
            translation = enriched;
          }
        }
        if (translation) {
          translation = translation.charAt(0).toUpperCase() + translation.slice(1);
        }
        const contextTranslation = context ? await translateText(context, 'en', 'pt') : undefined;

        console.log('Word data:', wordData);
        
        if (isMounted) {
          setDetails({
            word: wordData.word,
            phonetic: wordData.phonetic || '',
            definition: wordData.definition,
            translation,
            contextTranslation,
            audio: wordData.audio || '',
            examples: wordData.examples || []
          });
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching word details:', err);
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to fetch word details'
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchWordDetails();

    return () => {
      isMounted = false;
    };
  }, [word, context]);

  return { details, isLoading, error };
}